/**
 * Inloggegevens-blok voor /account: e-mail wijzigen (bevestiging via mail)
 * + wachtwoord wijzigen (met huidige-wachtwoord verificatie).
 */
import { useState } from "react";
import { KeyRound, Loader2, Mail } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

function Field({ label, type = "text", value, onChange, testid, placeholder, autocomplete }) {
  return (
    <div>
      <label className="stat-label">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autocomplete}
        data-testid={testid}
        className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary text-sm"
      />
    </div>
  );
}

export default function LoginCredentialsBlock({ currentEmail }) {
  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);

  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  const requestEmailChange = async () => {
    if (!newEmail || !emailPassword) {
      toast.error("Vul zowel het nieuwe e-mailadres als je huidige wachtwoord in.");
      return;
    }
    setEmailBusy(true);
    try {
      await api.post("/account/email/request-change", {
        new_email: newEmail,
        password: emailPassword,
      });
      toast.success(`Bevestigingsmail verstuurd naar ${newEmail}. Klik op de link om de wijziging door te voeren.`);
      setNewEmail("");
      setEmailPassword("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Verzoek mislukt.");
    } finally {
      setEmailBusy(false);
    }
  };

  const changePassword = async () => {
    if (!curPw || !newPw) {
      toast.error("Vul beide wachtwoordvelden in.");
      return;
    }
    if (newPw.length < 8) {
      toast.error("Nieuw wachtwoord moet minimaal 8 tekens hebben.");
      return;
    }
    if (newPw !== newPw2) {
      toast.error("Wachtwoorden komen niet overeen.");
      return;
    }
    setPwBusy(true);
    try {
      await api.post("/account/password", {
        current_password: curPw,
        new_password: newPw,
      });
      toast.success("Wachtwoord bijgewerkt.");
      setCurPw(""); setNewPw(""); setNewPw2("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Wachtwoord wijzigen mislukt.");
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <div className="border-2 border-foreground bg-card heavy-shadow p-6 md:p-8 space-y-6" data-testid="credentials-card">
      <div>
        <div className="flex items-center gap-2 stat-label">
          <KeyRound className="w-3.5 h-3.5" /> Inloggegevens
        </div>
        <h2 className="font-display font-black text-2xl uppercase mt-1">Beheer e-mail & wachtwoord</h2>
      </div>

      {/* E-mail wijzigen */}
      <div className="pb-6 border-b-2 border-border">
        <div className="flex items-center gap-2 mb-2">
          <Mail className="w-4 h-4" />
          <span className="font-semibold text-sm">E-mailadres wijzigen</span>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Huidig e-mailadres: <span className="font-mono-num" data-testid="credentials-current-email">{currentEmail || "—"}</span>.
          Je krijgt een bevestigingsmail op het nieuwe adres — pas na klikken op de link wordt de wijziging actief.
        </p>
        <div className="grid md:grid-cols-2 gap-3">
          <Field
            label="Nieuw e-mailadres"
            type="email"
            value={newEmail}
            onChange={setNewEmail}
            testid="credentials-new-email"
            placeholder="nieuw@voorbeeld.nl"
            autocomplete="email"
          />
          <Field
            label="Huidig wachtwoord"
            type="password"
            value={emailPassword}
            onChange={setEmailPassword}
            testid="credentials-email-password"
            autocomplete="current-password"
          />
        </div>
        <button
          type="button"
          onClick={requestEmailChange}
          disabled={emailBusy}
          data-testid="credentials-email-submit"
          className="mt-3 btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2 disabled:opacity-60"
        >
          {emailBusy && <Loader2 className="w-4 h-4 animate-spin" />}
          Verstuur bevestigingsmail
        </button>
      </div>

      {/* Wachtwoord wijzigen */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <KeyRound className="w-4 h-4" />
          <span className="font-semibold text-sm">Wachtwoord wijzigen</span>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Minimaal 8 tekens. Kies iets sterks — dit account beheert je financiële scenario's.
        </p>
        <div className="grid md:grid-cols-3 gap-3">
          <Field
            label="Huidig wachtwoord"
            type="password"
            value={curPw}
            onChange={setCurPw}
            testid="credentials-current-password"
            autocomplete="current-password"
          />
          <Field
            label="Nieuw wachtwoord"
            type="password"
            value={newPw}
            onChange={setNewPw}
            testid="credentials-new-password"
            autocomplete="new-password"
          />
          <Field
            label="Herhaal nieuw wachtwoord"
            type="password"
            value={newPw2}
            onChange={setNewPw2}
            testid="credentials-new-password-confirm"
            autocomplete="new-password"
          />
        </div>
        <button
          type="button"
          onClick={changePassword}
          disabled={pwBusy}
          data-testid="credentials-password-submit"
          className="mt-3 btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2 disabled:opacity-60"
        >
          {pwBusy && <Loader2 className="w-4 h-4 animate-spin" />}
          Wachtwoord opslaan
        </button>
      </div>
    </div>
  );
}
